import { FC, ReactElement, useEffect, useRef } from 'react';

import { IconButton, IconButtonSize } from '@pp/core/src/components/controls/iconButton/IconButton';
import { Icons } from '@pp/core/src/components/icons/iconNames';
import { useMedia } from '@pp/core/src/hooks/useMedia';

import { useGameDispatch } from '../../../store/GameStore';
import { useHighflyerGameStateSlice } from '../../../store/slices/highflyerGame/selectors';
import { highflyerGameStateActions } from '../../../store/slices/highflyerGame/slice';

import styles from './ScrollableWrapper.module.css';

interface ScrollableWrapperProps {
  children: ReactElement;
}

export const ScrollableWrapper: FC<ScrollableWrapperProps> = ({ children }): ReactElement => {
  const dispatch = useGameDispatch();
  const isTabScrollbarAtTop = useHighflyerGameStateSlice.isTabScrollbarAtTop();
  const currentPeriod = useHighflyerGameStateSlice.currentCalenderOption();
  const container = useRef<HTMLDivElement | null>(null);
  const throttleScroll = useRef<boolean>(false);
  const isWide = useMedia('(min-width: 1024px)');

  useEffect(() => {
    dispatch(highflyerGameStateActions.setTabScrollbarAtTop(false));
    if (container.current) {
      if (isWide) {
        container.current?.scrollTo({ top: 0 });
      } else if (container.current && !isWide) {
        container.current!.style.overflow = 'hidden';
        setTimeout(() => {
          if (container.current) {
            container.current!.style.overflow = 'auto';
            container.current?.scrollTo({ top: 0 });
          }
        }, 10);
      }
    }
  }, [currentPeriod, isWide]);

  useEffect(() => {
    container.current?.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.current?.removeEventListener('scroll', handleScroll);
  }, [container.current]);

  function scrollTop(): void {
    container.current?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }

  function handleScroll(): void {
    if (throttleScroll.current) return;
    throttleScroll.current = true;
    setTimeout(() => {
      dispatch(
        highflyerGameStateActions.setTabScrollbarAtTop(container.current ? container.current?.scrollTop > 0 : false)
      );
      throttleScroll.current = false;
    }, 100);
  }

  return (
    <div className={styles.overflowContainer} ref={container}>
      {children}
      <div className={styles.scrollButton} hidden={!isTabScrollbarAtTop}>
        <IconButton icon={Icons.ChevronUp} ghost={false} size={IconButtonSize.LARGE} onClick={scrollTop} />
      </div>
    </div>
  );
};
